import React from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from 'recharts'
import type { LeadTimeBucket } from '@/types/calendar'

interface LeadTimeChartProps {
  distribution: LeadTimeBucket[]
  currentLeadTime: number
}

const BUCKET_LABELS: Record<number, string> = {
  0: 'Same day',
  1: '1d',
  3: '2-3d',
  7: '4-7d',
  14: '1-2w',
  21: '2-3w',
  30: '3-4w',
  45: '1-2m',
  60: '2-3m',
  90: '3-4m',
  120: '4-6m',
  180: '6-12m',
  365: '12m+',
}

const DistTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null
  const d = payload[0]?.payload
  if (!d) return null
  return (
    <div className="rounded-lg border border-border/60 bg-white px-3 py-2 shadow-lg shadow-black/5">
      <p className="text-[11px] font-medium text-muted-foreground">{BUCKET_LABELS[d.days] ?? `${d.days}d+`}</p>
      <p className="text-sm font-semibold text-foreground">{d.count} booking{d.count !== 1 ? 's' : ''}</p>
    </div>
  )
}

export default function LeadTimeChart({ distribution, currentLeadTime }: LeadTimeChartProps) {
  const total = distribution.reduce((sum, b) => sum + b.count, 0)

  if (total === 0) {
    return (
      <div className="flex items-center justify-center h-[140px] text-xs text-muted-foreground">
        No booking lead time data available
      </div>
    )
  }

  const data = distribution.map((b) => ({
    ...b,
    label: BUCKET_LABELS[b.days] ?? `${b.days}d`,
    isCurrent: b.days <= currentLeadTime && (distribution.find((next) => next.days > b.days)?.days ?? Infinity) > currentLeadTime,
  }))

  return (
    <div>
      <ResponsiveContainer width="100%" height={140}>
        <BarChart data={data} barSize={18}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 9, fill: '#64748b' }}
            tickLine={false}
            axisLine={{ stroke: '#e2e8f0' }}
          />
          <YAxis
            tick={{ fontSize: 10, fill: '#64748b' }}
            tickLine={false}
            axisLine={false}
            width={30}
            allowDecimals={false}
          />
          <Tooltip content={<DistTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
          <Bar dataKey="count" radius={[3, 3, 0, 0]}>
            {data.map((entry, i) => (
              <Cell key={i} fill={entry.isCurrent ? '#0d9488' : '#94a3b8'} fillOpacity={entry.count > 0 ? 1 : 0.3} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="flex items-center justify-between text-[11px] text-muted-foreground mt-1">
        <span>{total} total bookings</span>
        <span>Now: {currentLeadTime === 0 ? 'booked same day' : `booked ${currentLeadTime} days out`}</span>
      </div>
    </div>
  )
}
